import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { emitter } from 'shared/helpers/mitt';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

Cookies.defaults = { sameSite: 'Lax' };

export const AUTH_COOKIE_NAME = 'cw_d_session_info';
export const AUTH_HEADER_KEYS = [
  'access-token',
  'client',
  'uid',
  'expiry',
  'token-type',
];

const authHeaderValue = (headers, key) =>
  (typeof headers?.get === 'function' ? headers.get(key) : undefined) ??
  headers?.[key] ??
  headers?.[key.toLowerCase()] ??
  headers?.[key.toUpperCase()];

export const normalizeAuthHeaders = headers => {
  const authData = AUTH_HEADER_KEYS.reduce((data, key) => {
    const value = authHeaderValue(headers, key);
    if (value !== undefined && value !== null && value !== '') {
      data[key] = String(value);
    }
    return data;
  }, {});

  return AUTH_HEADER_KEYS.every(key => Boolean(authData[key]))
    ? authData
    : null;
};

export const getAuthCookieData = () => {
  const savedAuthInfo = Cookies.get(AUTH_COOKIE_NAME);
  if (!savedAuthInfo) return null;

  try {
    const authData = normalizeAuthHeaders(JSON.parse(savedAuthInfo));
    if (!authData) {
      Cookies.remove(AUTH_COOKIE_NAME);
    }
    return authData;
  } catch {
    Cookies.remove(AUTH_COOKIE_NAME);
    return null;
  }
};

export const hasValidAuthCookie = () => Boolean(getAuthCookieData());

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = authData => fromUnixTime(Number(authData.expiry));

export const setAuthCredentials = response => {
  const authData = normalizeAuthHeaders(response?.headers || {});
  Cookies.remove(AUTH_COOKIE_NAME);
  if (!authData) {
    throw new Error('Authentication response is missing required headers');
  }

  const expiryDate = getHeaderExpiry(authData);
  Cookies.set(AUTH_COOKIE_NAME, JSON.stringify(authData), {
    expires: differenceInDays(expiryDate, new Date()),
  });
  setUser(response.data.data, expiryDate);
  return authData;
};

export const clearBrowserSessionCookies = () => {
  Cookies.remove(AUTH_COOKIE_NAME);
  Cookies.remove('auth_data');
  Cookies.remove('user');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

export const clearCookiesOnLogout = () => {
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink = globalConfig.LOGOUT_REDIRECT_LINK || '/';
  window.location = logoutRedirectLink;
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
